import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  CircleOutlined as StatusIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { getListTeacher, updateSpeakingStatusAdmin } from "../service";
import { User } from "entities/users/interface";

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
    {icon}
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  </Box>
);

const BookingItem = ({
  data,
  teachers,
  listUserFollow,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}) => {
  const getStatusColor = (status) => {
    const statusColors = {
      pending: "warning",
      approved: "success",
      canceled: "error",
      default: "default",
      testting: "default",
      tested: "default",
      successed: "default",
    };
    return statusColors[status] || statusColors.default;
  };

  const [localBooking, setLocalBooking] = useState(data);
  const [dataBooking, setDataBooking] = useState(data);

  function arraysEqual(arr1: any, arr2: any): boolean {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
      return false;
    }
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every(
      (value, index) => String(value) === String(sortedArr2[index])
    );
  }
  const hasChanges = useMemo(() => {
    return (
      localBooking.status !== dataBooking.status ||
      localBooking.teacher_id?._id !== dataBooking.teacher_id?._id ||
      !arraysEqual(localBooking.user_follow_id, dataBooking.user_follow_id)
    );
  }, [localBooking, dataBooking]);

  const handleTeacherChange = (event) => {
    const idTeacher = event.target.value;
    const newTeacher = teachers.find((user) => user._id === idTeacher);
    setLocalBooking((prev) => ({
      ...prev,
      teacher_id: newTeacher,
    }));
  };

  const handleStatusChange = (event) => {
    setLocalBooking((prev) => ({
      ...prev,
      status: event.target.value,
    }));
  };

  // const onUpdate = ({ status, teacher_id }) => {
  //   console.log({
  //     status: localBooking.status,
  //     teacher_id: localBooking.teacher_id,
  //   });
  // };

  const handleUpdate = async () => {
    const data = {
      _id: localBooking._id,
      status: localBooking.status,
      teacher_id: localBooking.teacher_id?._id || null,
      user_follow_id: localBooking.user_follow_id || [],
    };

    try {
      const res: any = await updateSpeakingStatusAdmin(data);
      const newDataBooking = res.data;

      console.log("res.update..", newDataBooking);
      setDataBooking(newDataBooking);
      setSnackbarMessage("Cập nhật trạng thái thành công!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Cập nhật trạng thái thất bại!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Card sx={{ maxWidth: 800, width: "100%" }}>
      <CardContent>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={<PersonIcon color="action" />}
              label="Họ và tên"
              value={data.full_name}
            />
            <InfoRow
              icon={<EmailIcon color="action" />}
              label="Email"
              value={data.email}
            />
            <InfoRow
              icon={<PhoneIcon color="action" />}
              label="Số điện thoại"
              value={data.phone_number}
            />
            <InfoRow
              icon={<EventIcon color="action" />}
              label="Thời gian"
              value={format(new Date(data.date_time), "HH:mm dd/MM/yyyy")}
            />
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={<PersonIcon color="action" />}
              label="Học sinh"
              value={data?.student_id?.display_name || ""}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PersonIcon color="action" />
              <Box sx={{ flexGrow: 1 }}>
                <TeacherAutocomplete
                  listUserFollow={listUserFollow}
                  localBooking={localBooking}
                  setLocalBooking={setLocalBooking}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PersonIcon color="action" />
              <Box sx={{ flexGrow: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Giáo viên</InputLabel>
                  <Select
                    value={localBooking.teacher_id?._id || ""}
                    onChange={handleTeacherChange}
                    label="Teacher"
                  >
                    <MenuItem value="">
                      <em>Not assigned</em>
                    </MenuItem>
                    {teachers.map((teacher) => (
                      <MenuItem key={teacher._id} value={teacher._id}>
                        {teacher.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <StatusIcon color="action" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Trạng thái
                </Typography>
                <Box>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      mt: 0.5,
                      borderColor: getStatusColor(localBooking.status),
                    }}
                  >
                    <Select
                      value={localBooking.status || "pending"}
                      onChange={handleStatusChange}
                      size="small"
                      sx={{ height: 32 }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="canceled">Canceled</MenuItem>
                      <MenuItem value="testting">Testing</MenuItem>
                      <MenuItem value="tested">Tested</MenuItem>
                      <MenuItem value="successed">Successed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Update Button */}
            {hasChanges && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdate}
                  size="small"
                  sx={{
                    backgroundColor: "#CC2027",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#B01723",
                    },
                  }}
                >
                  Cập nhật
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

interface LocalBooking {
  user_follow_id: string[];
}

interface Props {
  localBooking: LocalBooking;
  setLocalBooking: React.Dispatch<React.SetStateAction<LocalBooking>>;
  listUserFollow: User[];
}
const TeacherAutocomplete: React.FC<Props> = ({
  localBooking,
  setLocalBooking,
  listUserFollow,
}) => {
  const [open, setOpen] = React.useState(false);
  const [userFollow, setUserFollow] = React.useState<User[]>(
    (listUserFollow ?? []).filter((u) =>
      (localBooking?.user_follow_id ?? []).includes(u._id)
    )
  );
  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFollowChange = (event: any, value: User[]) => {
    setUserFollow(value);
    setLocalBooking((prev) => ({
      ...prev,
      user_follow_id: value.map((user) => user._id),
    }));
  };

  const selectedTeachers = (listUserFollow ?? []).filter((teacher) =>
    userFollow?.some((u) => u._id === teacher._id)
  );

  return (
    <Autocomplete
      multiple
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={listUserFollow}
      value={userFollow}
      onChange={handleFollowChange}
      getOptionLabel={(option) => option.display_name}
      renderOption={(props, option) => {
        const isSelected = userFollow.some((user) => user._id === option._id);

        return (
          <li
            {...props}
            key={option._id}
            style={{
              fontWeight: isSelected ? "bold" : "normal",
              background: isSelected ? "#f0f0f0" : "transparent",
            }}
            onClick={() => {
              const updatedSelection = isSelected
                ? userFollow.filter((user) => user._id !== option._id) // Nếu đã chọn, thì xóa khỏi danh sách
                : [...userFollow, option]; // Nếu chưa chọn, thì thêm vào danh sách

              setUserFollow(updatedSelection);

              setLocalBooking((prev) => ({
                ...prev,
                user_follow_id: updatedSelection.map((user) => user._id),
              }));
            }}
          >
            {option.display_name} {isSelected ? "(Đã chọn)" : ""}
          </li>
        );
      }}
      ListboxProps={{
        style: { maxHeight: "150px", overflowY: "auto" },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Người theo dõi"
          placeholder="Tìm kiếm..."
        />
      )}
    />
  );
};

export default BookingItem;
